import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/dateUtils';

const TimesheetEntryForm = ({ isOpen, onClose, onSubmit, contractData, vaProfile, projects = [] }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    description: '',
    task_completed: '',
    is_billable: true,
    project_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const calculateHours = () => {
    if (!formData.start_time || !formData.end_time) return 0;
    
    const start = new Date(`${formData.date}T${formData.start_time}`);
    const end = new Date(`${formData.date}T${formData.end_time}`);
    
    if (end <= start) return 0;
    
    const diffMs = end - start;
    return Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100; // Round to 2 decimal places
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const hours_worked = calculateHours();
      
      if (hours_worked <= 0) {
        throw new Error('End time must be after start time');
      }

      if (hours_worked > 24) {
        throw new Error('Cannot log more than 24 hours in a single day');
      }

      const entryData = {
        ...formData,
        start_time: new Date(`${formData.date}T${formData.start_time}`).toISOString(),
        end_time: new Date(`${formData.date}T${formData.end_time}`).toISOString(),
        hours_worked
      };

      await onSubmit(entryData);
      
      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '17:00',
        description: '',
        task_completed: '',
        is_billable: true,
        project_id: ''
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const hourlyRate = contractData?.hourly_rate || vaProfile?.hourly_rate || 0;
  const estimatedAmount = calculateHours() * hourlyRate;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Time Entry"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              required
              max={new Date().toISOString().split('T')[0]} // Can't log future dates
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Hours: {calculateHours()}h
            </label>
            <div className="text-sm text-text-secondary">
              Estimated: ${estimatedAmount.toFixed(2)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={formData.start_time}
              onChange={(e) => handleInputChange('start_time', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              End Time
            </label>
            <input
              type="time"
              value={formData.end_time}
              onChange={(e) => handleInputChange('end_time', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>
        </div>

        {/* Project Selection */}
        {projects && projects.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Project (Optional)
            </label>
            <select
              value={formData.project_id}
              onChange={(e) => handleInputChange('project_id', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select a project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                  {project.client_name && ` - ${project.client_name}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            rows={3}
            placeholder="Describe what you worked on..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Tasks Completed
          </label>
          <textarea
            value={formData.task_completed}
            onChange={(e) => handleInputChange('task_completed', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            rows={2}
            placeholder="List specific tasks or deliverables completed..."
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_billable"
            checked={formData.is_billable}
            onChange={(e) => handleInputChange('is_billable', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
          />
          <label htmlFor="is_billable" className="ml-2 block text-sm text-text-primary">
            This time is billable
          </label>
        </div>

        {error && (
          <div className="text-error text-sm bg-error-50 border border-error-200 rounded-lg p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={calculateHours() <= 0}
          >
            Add Entry
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TimesheetEntryForm;
